import { useCallback, useEffect, useRef, useState } from 'react';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

/**
 * Detect whether the renderer is hosted in a macOS BrowserWindow. The
 * Foundry shell uses the platform-default title-bar style, so on darwin
 * the top-left ~78px of the window chrome is reserved for the
 * traffic-light controls (red/yellow/green close/minimise/zoom buttons).
 * Banners that span the full width need to inset their leading content
 * so the text doesn't render UNDERNEATH the traffic lights.
 *
 * Detection uses `navigator.platform` because the renderer process
 * doesn't have direct access to `process.platform` (contextIsolation
 * sandboxes it away). Mirrors the same pattern as `PrereqStatusList`.
 *
 * Issue #504: alpha.72 update banner had "Foundry vX.Y.Z is ready to
 * install." rendered with its leading "Foundry" word obscured by the
 * red close button on macOS. Cheap CSS-only fix — conditional left pad.
 */
function isDarwin(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /mac|darwin/i.test(navigator.platform);
}

/**
 * Top-of-window banner shown when a new version has been downloaded in the
 * background and is ready to install. Wired to the `app:update-downloaded`
 * push channel from the main process (electron-updater `update-downloaded`
 * event).
 */
interface UpdateBannerProps {
  /** When true and newUi is active, suppresses the banner so it can be
   *  rendered inside the Dashboard layout instead of at the App shell. */
  fromAppShell?: boolean;
}

/**
 * Re-prompt defer floor for the "Install and restart" banner (issue #635).
 *
 * Clicking "Later" hides the banner, but a user who defers and keeps Foundry
 * running can sit on a stale in-memory bundle for hours — hitting bugs already
 * fixed in the downloaded build (PJ's `agent.questions` repro, 2026-05-13:
 * hours on a pre-alpha.76 bundle after .78 had already downloaded). To shrink
 * that "downloaded but not running" gap without nagging, the banner
 * re-surfaces the SAME deferred version when the window regains focus — but
 * only once at least this long has elapsed since the dismissal, so rapid
 * alt-tabbing never re-nags. A brand-new downloaded version always re-shows
 * immediately (handled by the `app:update-downloaded` push), independent of
 * this floor.
 *
 * 30 minutes: long enough that returning to Foundry after a break re-shows the
 * one-click restart at a natural pause point; short enough that an hours-long
 * session doesn't silently stay stale. Mirrors the main-process
 * `UPDATE_RECHECK_FLOOR_MS` focus-throttle rationale (issue #189).
 */
export const UPDATE_REPROMPT_FLOOR_MS = 30 * 60 * 1000;

export function UpdateBanner({ fromAppShell }: UpdateBannerProps = {}): React.JSX.Element | null {
  const { newUi } = useFeatureFlags();
  const [version, setVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);
  /**
   * Issue #3584: the install can be REFUSED — most notably when Foundry runs
   * from a macOS App Translocation mount, where the main process rejects
   * `app:install-update` with the move-to-/Applications remedy. Pre-fix the
   * rejection only flipped `installing` back to false, so the button silently
   * reverted to "Install and restart" and the user re-clicked forever with no
   * idea why nothing happened. Holds the DECODED message (see `handleInstall`)
   * — main-process copy written for the user, rendered verbatim once the IPC
   * envelope is peeled off.
   */
  const [installError, setInstallError] = useState<string | null>(null);

  // Issue #635: epoch millis of the last "Later" dismissal, so a focus-regain
  // can decide whether enough time has passed to re-surface the banner. A ref
  // (not state) — reading it in the focus handler must not re-subscribe the
  // listener, and writing it must not trigger a re-render.
  const dismissedAtRef = useRef<number | null>(null);

  const handleDismiss = useCallback(() => {
    dismissedAtRef.current = Date.now();
    setDismissed(true);
  }, []);

  useEffect(() => {
    // Live updates while this window is mounted.
    const unsubscribe = window.crucible.on('app:update-downloaded', (payload) => {
      setVersion(payload.version);
      setDismissed(false);
      // A newly-downloaded version is a fresh prompt — clear any prior
      // dismissal so the focus-regain floor (#635) starts from this show.
      dismissedAtRef.current = null;
    });
    // Catch-up path: if the download completed before this window existed
    // (e.g. macOS dock: window was closed while alpha.N was downloading),
    // fetch the cached pending update on mount.
    window.crucible
      .invoke('app:get-pending-update')
      .then((pending) => {
        if (pending) setVersion(pending.version);
      })
      .catch(() => {
        /* best-effort */
      });
    return unsubscribe;
  }, []);

  useEffect(() => {
    // Issue #635: re-prompt at a natural pause point. When the user returns to
    // Foundry (window focus) after clicking "Later", re-show the banner for the
    // still-pending version — but only once UPDATE_REPROMPT_FLOOR_MS has
    // elapsed since the dismissal, so frequent alt-tabbing never re-nags.
    const onFocus = (): void => {
      const dismissedAt = dismissedAtRef.current;
      if (dismissedAt === null) return;
      if (Date.now() - dismissedAt < UPDATE_REPROMPT_FLOOR_MS) return;
      dismissedAtRef.current = null;
      setDismissed(false);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (!version || dismissed) return null;
  if (fromAppShell && newUi) return null;

  const handleInstall = () => {
    setInstalling(true);
    setInstallError(null);
    window.crucible.invoke('app:install-update').catch((err: unknown) => {
      setInstalling(false);
      // `createHandleInvoke` flattens every main-process throw through
      // `flattenIpcError`, and Electron prepends "Error invoking remote
      // method '…':" on top of that — so `err.message` is the raw
      // `[CRUCIBLE_ERR_V1]{…}` envelope, not the remedy copy. Decode it the
      // way every other renderer surface does (UpdateTemplateModal,
      // OnboardingOverlay, WorkspaceHeader, Settings).
      setInstallError(unflattenIpcError(err).message);
    });
  };

  // Issue #504. On macOS, reserve ~78px of left inset for the
  // traffic-light controls so the banner copy doesn't render
  // underneath the red close button. Linux + Windows don't have the
  // inset so they keep the default `px-6`.
  const darwin = isDarwin();
  const platformPadLeft = darwin ? 'pl-20 pr-6' : 'px-6';

  if (newUi) {
    return (
      <div
        data-testid="update-banner"
        data-platform-darwin={darwin ? 'true' : 'false'}
        className={`flex flex-none items-center justify-between py-2.5 mb-4 f-body ${platformPadLeft}`}
        style={{
          background: 'var(--bg-elev-2)',
          borderBottom: '1px solid var(--border-alpha-1)',
          borderLeft: '3px solid rgba(33, 69, 247, 0.7)',
          color: 'var(--text-1)',
        }}
      >
        <div className="flex flex-col">
          <span>
            <span className="font-medium">Foundry v{version}</span> is ready to install.
          </span>
          {installError && (
            <span data-testid="update-banner-error" style={{ color: 'var(--color-error)' }}>
              {installError}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleInstall}
            disabled={installing}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
            style={{ background: 'var(--color-crucible-accent, #2145F7)' }}
          >
            {installing ? 'Installing…' : 'Install and restart'}
          </button>
          <button
            onClick={handleDismiss}
            className="rounded-lg px-2 py-1 text-xs"
            style={{ color: 'var(--text-3)' }}
          >
            Later
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="update-banner"
      data-platform-darwin={darwin ? 'true' : 'false'}
      className={`flex flex-none items-center justify-between border-b border-crucible-accent/40 bg-crucible-accent/15 py-2.5 text-sm text-crucible-fg ${platformPadLeft}`}
    >
      <div className="flex flex-col">
        <span>
          <span className="font-medium">Foundry v{version}</span> is ready to install.
        </span>
        {installError && (
          <span data-testid="update-banner-error" className="text-xs text-red-300">
            {installError}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={handleInstall}
          disabled={installing}
          className="rounded-md bg-crucible-accent px-3 py-1 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
        >
          {installing ? 'Installing…' : 'Install and restart'}
        </button>
        <button
          onClick={handleDismiss}
          className="rounded-md px-3 py-1 text-sm text-white/60 hover:text-white/80"
        >
          Later
        </button>
      </div>
    </div>
  );
}
